// Structural shapes shared by both apps' generated types, so these helpers serve
// the management and field clients without depending on either.
export type ServiceWithMaterials = {
  materials?: Array<{ material?: string | null }> | null;
};

export type MaterialUsage = {
  material?: string | null;
  amount?: string | null;
  batch?: string | null;
};

type ServiceLookup = (serviceId: string) => ServiceWithMaterials | undefined;

export const getServiceMaterialIds = (service?: ServiceWithMaterials | null): string[] =>
  (service?.materials ?? [])
    .map((serviceMaterial) => serviceMaterial.material)
    .filter((materialId): materialId is string => !!materialId);

export const mergeMaterialIds = (existing: string[], additions: string[]): string[] => [
  ...new Set([...existing, ...additions]),
];

export const getMaterialUsageIds = (usages: MaterialUsage[]): string[] =>
  usages
    .map((usage) => usage.material)
    .filter((materialId): materialId is string => !!materialId);

// A supply counts as used once an amount or a batch is recorded for it.
export const isMaterialUsageRecorded = (usage: MaterialUsage): boolean =>
  usage.amount != null || usage.batch != null;

export const getUsedMaterialIds = (usages: MaterialUsage[]): Set<string> =>
  new Set(getMaterialUsageIds(usages.filter(isMaterialUsageRecorded)));

// Once the work is finished the supplies list is a record of what was used, not a
// to-do list: planned supplies that were never used are hidden by default and the
// field app stops accepting changes.
export const isSupplyRecordingClosed = (status: string | null | undefined): boolean =>
  status === "IN_REVIEW" || status === "DONE";

export const collectServiceIds = (
  workOrderServiceId: string | null | undefined,
  items: Array<{ service?: string | null } | undefined> | undefined
): string[] =>
  [workOrderServiceId, ...(items ?? []).map((item) => item?.service)].filter(
    (serviceId): serviceId is string => !!serviceId
  );

const materialIdsOfServices = (serviceIds: string[], findService: ServiceLookup) =>
  new Set(
    serviceIds.flatMap((serviceId) => getServiceMaterialIds(findService(serviceId)))
  );

// Adds materials of services that appeared on the work order and removes materials of
// services that left it, unless another remaining service still needs them or they
// have recorded usage. Materials unrelated to the changed services are left alone.
export const reconcileServiceMaterials = ({
  materialIds,
  previousServiceIds,
  nextServiceIds,
  findService,
  usedMaterialIds,
}: {
  materialIds: string[];
  previousServiceIds: string[];
  nextServiceIds: string[];
  findService: ServiceLookup;
  usedMaterialIds: Set<string>;
}): string[] => {
  const previousServiceIdSet = new Set(previousServiceIds);
  const nextServiceIdSet = new Set(nextServiceIds);
  const requiredMaterialIds = materialIdsOfServices(nextServiceIds, findService);
  const droppedServiceMaterialIds = materialIdsOfServices(
    previousServiceIds.filter((serviceId) => !nextServiceIdSet.has(serviceId)),
    findService
  );
  const addedServiceMaterialIds = materialIdsOfServices(
    nextServiceIds.filter((serviceId) => !previousServiceIdSet.has(serviceId)),
    findService
  );

  const keptMaterialIds = materialIds.filter(
    (materialId) =>
      !droppedServiceMaterialIds.has(materialId) ||
      requiredMaterialIds.has(materialId) ||
      usedMaterialIds.has(materialId)
  );
  return mergeMaterialIds(keptMaterialIds, [...addedServiceMaterialIds]);
};

// For saves that change a stored ticket's tasks: the ticket's material ids after the
// task change, or undefined when the material list stays the same.
export const reconcileTicketMaterialIds = (
  ticket: {
    service?: { id?: string } | null;
    items: Array<{ service?: string | null }>;
    materials: MaterialUsage[];
  },
  updatedItems: Array<{ service?: string | null }>,
  findService: ServiceLookup
): string[] | undefined => {
  const currentMaterialIds = getMaterialUsageIds(ticket.materials);
  const materialIds = reconcileServiceMaterials({
    materialIds: currentMaterialIds,
    previousServiceIds: collectServiceIds(ticket.service?.id, ticket.items),
    nextServiceIds: collectServiceIds(ticket.service?.id, updatedItems),
    findService,
    usedMaterialIds: getUsedMaterialIds(ticket.materials),
  });
  const unchanged =
    materialIds.length === currentMaterialIds.length &&
    materialIds.every((materialId, index) => materialId === currentMaterialIds[index]);
  return unchanged ? undefined : materialIds;
};
